import { SettingOutlined } from '@ant-design/icons';
import cn from 'classnames';
import { useMemo } from 'react';
import { generatePath, Link, matchPath, useLocation } from 'react-router-dom';

import { useLayoutModalStore } from '@/features/cex-pair-statistic';

import { ROUTE_PATHS } from 'shared/consts/routes';

interface PairViewToggleProps {
  projectId: number;
  pairId: number;
  venue: 'cex' | 'dex' | 'sol';
}

export const PairViewToggle = ({ projectId, pairId, venue }: PairViewToggleProps) => {
  const location = useLocation();
  const { openLayoutModal } = useLayoutModalStore();

  const { currentPath, v2Path } = useMemo(() => {
    const projectIdStr = projectId.toString();
    const pairIdStr = pairId.toString();

    return {
      currentPath: generatePath(ROUTE_PATHS.cexPair, {
        projectId: projectIdStr,
        pairId: pairIdStr,
      }),
      v2Path: generatePath(ROUTE_PATHS.pair, {
        projectId: projectIdStr,
        pairId: pairIdStr,
        venue,
      }),
    };
  }, [pairId, projectId, venue]);

  const isCurrentActive = useMemo(() => {
    const match = matchPath(ROUTE_PATHS.cexPair, location.pathname);
    return (
      Boolean(match) &&
      match?.params?.projectId === projectId.toString() &&
      match?.params?.pairId === pairId.toString()
    );
  }, [location.pathname, pairId, projectId]);

  const isV2Active = useMemo(() => {
    const match = matchPath(ROUTE_PATHS.pair, location.pathname);
    return (
      Boolean(match) &&
      match?.params?.projectId === projectId.toString() &&
      match?.params?.pairId === pairId.toString() &&
      match?.params?.venue?.toLowerCase() === venue
    );
  }, [location.pathname, pairId, projectId, venue]);

  const buttonBase =
    'px-3 py-1 text-xs font-semibold uppercase tracking-wide rounded-md border border-transparent transition-colors duration-150';

  return (
    <div className="ml-3 flex items-center gap-1 rounded-lg border border-[#1f2535] bg-[rgba(var(--bg-primary))] p-1">
      <Link
        to={currentPath}
        className={cn(buttonBase, {
          'bg-primary text-bg-primary ring-1 ring-primary': isCurrentActive,
          'text-gray-1 hover:bg-[#20263a]': !isCurrentActive,
        })}
      >
        Current
      </Link>
      <Link
        to={v2Path}
        className={cn(buttonBase, {
          'bg-primary text-bg-primary ring-1 ring-primary': isV2Active,
          'text-gray-1 hover:bg-[#20263a]': !isV2Active,
        })}
      >
        Beta
      </Link>
      {isV2Active && (
        <button
          onClick={openLayoutModal}
          className={cn(
            buttonBase,
            'flex items-center justify-center text-gray-1 hover:bg-[#20263a]',
          )}
          title="Layout Settings"
        >
          <SettingOutlined />
        </button>
      )}
    </div>
  );
};
